/* eslint-disable no-console */
import {
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ElementRef,
  QueryList,
  signal,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, animationFrameScheduler, fromEvent } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  tap,
  throttleTime,
  withLatestFrom
} from 'rxjs/operators';
import { GlobalFacade } from '~app/app/~store/facades/global.facade';
import { MatSidenav } from '@angular/material/sidenav';
import { MatListItem } from '@angular/material/list';
import { NavigationStart, Route, Router } from '@angular/router';
import { EventService } from '../../services/event.service';
import { SIDEBAR_LINKS, SidebarLink } from '../../constants/layout.constants';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatListModule } from '@angular/material/list';
import { SharedModule } from '../../modules/shared.module';
import { MatExpansionPanel } from '@angular/material/expansion';

@Component({
  selector: 'content-wrapper',
  standalone: true,
  imports: [
    MatToolbarModule,
    MatSidenavModule,
    MatTooltipModule,
    SharedModule,
    MatListModule
  ],
  templateUrl: './content-wrapper.component.html',
  styleUrls: ['./content-wrapper.component.scss']
})
export class ContentWrapperComponent {
  @ViewChild(MatSidenav) drawer: MatSidenav | undefined;
  @ViewChild('toolbar') toolbar: ElementRef<HTMLElement> | undefined;
  @ViewChildren(MatListItem) listItems: QueryList<MatListItem> | undefined;
  @ViewChildren('panel') panels!: QueryList<MatExpansionPanel>;

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  isWindowAtTop$ = fromEvent(window, 'scroll').pipe(
    startWith(false),
    throttleTime(0, animationFrameScheduler),
    map(() => window.scrollY === 0),
    distinctUntilChanged()
  );

  isLayoutHidden$ = this.globalFacade.routeData$.pipe(
    map(routeData =>
      routeData ? routeData.data?.layoutHidden === true : false
    ),
    startWith(true)
  );

  closeHandsetSidebarOnRouteChange$ = this.router.events.pipe(
    withLatestFrom(this.isHandset$),
    filter(([a, b]) => b && a instanceof NavigationStart),
    tap(() => this.drawer?.close())
  );

  onToggleMenu$ = this.eventService.events$.pipe(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    filter(ev => ev.type === 'TOGGLE_MENU'),
    withLatestFrom(this.isHandset$),
    tap(() => this.drawer?.toggle())
  );

  sidebarLinks: SidebarLink[] = SIDEBAR_LINKS;
  isSidenavCollapsed = false;
  routes: Route[] = [];

  readonly panelOpenState = signal(false);

  constructor(
    private breakpointObserver: BreakpointObserver,
    private globalFacade: GlobalFacade,
    private eventService: EventService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private appRef: ApplicationRef,
    private cdr: ChangeDetectorRef
  ) {}

  get toolbarHeight(): number | undefined {
    if (!this.toolbar?.nativeElement) return;
    return this.toolbar.nativeElement.clientHeight || 0;
  }

  toggleSidenav(): void {
    this.isSidenavCollapsed = !this.isSidenavCollapsed;
    this.panels.filter(panel => panel.expanded).forEach(panel => panel.close());

    this.cdr.detectChanges();
    setTimeout(() => {
      this.appRef.tick();
    }, 100);
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  onSidebarLinkClick(link: SidebarLink): void {
    if (link.children?.length) {
      if (this.isSidenavCollapsed) {
        this.isSidenavCollapsed = false;
        this.cdr.detectChanges();
      }
      this.panelOpenState.set(!this.panelOpenState());
    }
  }
}
